import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useSupabaseClient } from 'src/api/supabase'
import { queryKeys } from 'src/api/db/queryKeys'

interface VendorPrintCode {
  vendor: string
  print_code: string
}

const PAGE_SIZE = 1000

async function fetchAllPairs(
  supabase: ReturnType<typeof useSupabaseClient>,
  table: 'decoration_setup_cost' | 'decoration_unit_cost'
): Promise<VendorPrintCode[]> {
  const pairs: VendorPrintCode[] = []
  let from = 0
  let hasMore = true

  while (hasMore) {
    const { data, error } = await supabase
      .from(table)
      .select('vendor, print_code')
      .not('vendor', 'is', null)
      .not('print_code', 'is', null)
      .order('vendor')
      .order('print_code')
      .range(from, from + PAGE_SIZE - 1)
    if (error) throw error

    for (const row of data ?? []) {
      pairs.push({ vendor: row.vendor!, print_code: row.print_code! })
    }
    hasMore = (data?.length ?? 0) >= PAGE_SIZE
    from += PAGE_SIZE
  }

  return pairs
}

async function fetchProductPairs(
  supabase: ReturnType<typeof useSupabaseClient>
): Promise<VendorPrintCode[]> {
  const pairs: VendorPrintCode[] = []
  let from = 0
  let hasMore = true

  while (hasMore) {
    const { data, error } = await supabase
      .from('product')
      .select('source, print_codes')
      .not('source', 'is', null)
      .not('print_codes', 'is', null)
      .range(from, from + PAGE_SIZE - 1)
    if (error) throw error

    for (const row of data ?? []) {
      const source = row.source as string
      const printCodes = row.print_codes
      if (!source || !Array.isArray(printCodes)) continue

      for (const code of printCodes) {
        if (typeof code === 'string' && code.trim()) {
          pairs.push({ vendor: source, print_code: code.trim() })
        }
      }
    }

    hasMore = (data?.length ?? 0) >= PAGE_SIZE
    from += PAGE_SIZE
  }

  return pairs
}

function dedup(pairs: VendorPrintCode[]): VendorPrintCode[] {
  const seen = new Set<string>()
  return pairs.filter((p) => {
    const key = `${p.vendor}|${p.print_code}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

export const useOrphanPrintCodes = () => {
  const supabase = useSupabaseClient()

  const { data: setupPairs } = useQuery({
    queryKey: [queryKeys.decorations, 'orphan', 'setupPairs'],
    queryFn: () => fetchAllPairs(supabase, 'decoration_setup_cost'),
  })

  const { data: unitPairs } = useQuery({
    queryKey: [queryKeys.decorations, 'orphan', 'unitPairs'],
    queryFn: () => fetchAllPairs(supabase, 'decoration_unit_cost'),
  })

  const { data: productPairs } = useQuery({
    queryKey: [queryKeys.decorations, 'orphan', 'productPairs'],
    queryFn: () => fetchProductPairs(supabase),
  })

  const { data: infoPrintCodes } = useQuery({
    queryKey: [queryKeys.decorations, 'orphan', 'infoPrintCodes'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('decoration_info')
        .select('print_codes')
      if (error) throw error

      const allCodes = new Set<string>()
      for (const row of data ?? []) {
        for (const code of row.print_codes ?? []) {
          allCodes.add(code)
        }
      }

      return allCodes
    },
  })

  const allPairs = useMemo(
    () => dedup([...(setupPairs ?? []), ...(unitPairs ?? []), ...(productPairs ?? [])]).sort(
      (a, b) => a.vendor.localeCompare(b.vendor) || a.print_code.localeCompare(b.print_code)
    ),
    [setupPairs, unitPairs, productPairs]
  )

  const setupKeys = useMemo(
    () => new Set((setupPairs ?? []).map((p) => `${p.vendor}|${p.print_code}`)),
    [setupPairs]
  )

  const unitKeys = useMemo(
    () => new Set((unitPairs ?? []).map((p) => `${p.vendor}|${p.print_code}`)),
    [unitPairs]
  )

  const missingFromInfo = useMemo(
    () => allPairs.filter((p) => !infoPrintCodes?.has(p.print_code)),
    [allPairs, infoPrintCodes]
  )

  const missingFromUnitCost = useMemo(
    () => allPairs.filter((p) => !unitKeys.has(`${p.vendor}|${p.print_code}`)),
    [allPairs, unitKeys]
  )

  const missingFromSetupCost = useMemo(
    () => allPairs.filter((p) => !setupKeys.has(`${p.vendor}|${p.print_code}`)),
    [allPairs, setupKeys]
  )

  const hasInfo = useMemo(
    () => allPairs.filter((p) => infoPrintCodes?.has(p.print_code)),
    [allPairs, infoPrintCodes]
  )

  const hasUnitCost = useMemo(
    () => allPairs.filter((p) => unitKeys.has(`${p.vendor}|${p.print_code}`)),
    [allPairs, unitKeys]
  )

  const hasSetupCost = useMemo(
    () => allPairs.filter((p) => setupKeys.has(`${p.vendor}|${p.print_code}`)),
    [allPairs, setupKeys]
  )

  return {
    missingFromInfo,
    missingFromUnitCost,
    missingFromSetupCost,
    hasInfo,
    hasUnitCost,
    hasSetupCost,
  }
}
